import {
  ConfirmModal,
  EditPencil,
  OverflowTooltip,
  TablePaginationContext,
  UnLink,
  UserAvatar,
} from '@/components';
import { RoleColumn } from '@/components/Columns/RoleColumn';
import { PagedTable } from '@/components/PagedTable/PagedTable';
import { SortableColumnDef } from '@/components/TableV2/SortableTable';
import { ToggleColumn } from '@/components/ToggleColumn';
import { RoutePath } from '@/core/router';
import { useToggleUserAccess } from '@/hooks/use-toggle-user-access';
import { useToggleUserStatus } from '@/hooks/use-toggle-user-status';
import { useDisassociateBusinessAreaUser } from '@/modules/BusinessAreas/queries/disassociate-user';
import { BusinessAreaEmployee } from '@/modules/Users/domain';
import { EditBusinessAreaRoleModal } from '@/modules/Users/pages/UserDetailPage/components/EditBusinessAreaUserRoleModal';
import { useGetUsersByBusinessArea } from '@/modules/Users/queries';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BusinessAreaEmployeesTabActions } from '../BusinessAreaEmployeesTabActions/BusinessAreaEmployeesTabActions';

type BusinessAreaEmployeesTabProps = {
  businessAreaId: number;
};

export const BusinessAreaEmployeesTab = ({
  businessAreaId,
}: BusinessAreaEmployeesTabProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [unlinkSelectedUserId, setUnlinkSelectedUserId] = useState<number>();
  const [editSelectedUser, setEditSelectedUser] = useState<{
    id: number;
    role: number;
  }>();
  const { page, searchTerm, tableSort } = useContext(TablePaginationContext);
  const sort = tableSort[0];

  const { data: employees, isLoading } = useGetUsersByBusinessArea({
    businessAreaId,
    filter: searchTerm,
    pageNumber: page,
    sortBy: sort?.columnId,
    sortOrder: sort?.direction,
  });

  const { mutate: disassociateUserBusinessArea } =
    useDisassociateBusinessAreaUser();

  const { onChange: onStatusChange } = useToggleUserStatus();
  const { onChange: onAccessChange } = useToggleUserAccess();

  const columns = useMemo<SortableColumnDef<BusinessAreaEmployee>[]>(
    () => [
      {
        id: 'name',
        name: t('name'),
        accessor: 'fullName',
        alignment: 'left',
        tdClassNames: 'max-w-[15vh]',
        renderRow: (row) => {
          return (
            <div
              className="flex items-center gap-2.5 overflow-hidden"
              onClick={() => navigate(RoutePath.userDetail(row.id.toString()))}
            >
              <UserAvatar
                size="small"
                name={row.fullName}
                imageId={row.profileImageFileId}
              />
              <OverflowTooltip text={row.fullName} textClassName="font-black" />
            </div>
          );
        },
      },
      {
        id: 'email',
        name: t('email'),
        accessor: 'email',
      },
      {
        id: 'status',
        name: t('status'),
        accessor: 'status',
        renderRow: (row) => (
          <ToggleColumn
            onChange={() => onStatusChange(!row.status, row.id)}
            row={row}
            value={row.status}
          />
        ),
      },
      {
        id: 'access',
        name: t('access'),
        accessor: 'access',
        renderRow: (row) => (
          <ToggleColumn
            onChange={() => onAccessChange(!row.access, row.id)}
            row={row}
            value={row.access}
          />
        ),
      },
      {
        id: 'role',
        name: t('role'),
        accessor: 'roleName',
        renderRow: (row) => (
          <RoleColumn roleName={row.roleName} roleEntity="business-area" />
        ),
      },
      {
        id: 'actions',
        name: '',
        renderRow: (row) => (
          <div className="flex items-center gap-2">
            <EditPencil
              className="cursor-pointer"
              onClick={() => {
                setEditSelectedUser({ id: row.id, role: row.roleId });
              }}
            />
            <UnLink
              className="cursor-pointer"
              onClick={() => {
                setUnlinkSelectedUserId(row.id);
              }}
            />
          </div>
        ),
      },
    ],
    [navigate, onAccessChange, onStatusChange, t],
  );

  return (
    <>
      <PagedTable
        title={t('employees')}
        page={employees}
        isLoading={isLoading}
        columns={columns}
        emptyStateMessage={t('no_employees_available')}
        actions={
          <BusinessAreaEmployeesTabActions businessAreaId={businessAreaId} />
        }
      />
      {!!editSelectedUser && (
        <EditBusinessAreaRoleModal
          isOpen={!!editSelectedUser}
          handleClose={() => setEditSelectedUser(undefined)}
          user={editSelectedUser}
          businessAreaId={businessAreaId}
        />
      )}
      <ConfirmModal
        isOpen={!!unlinkSelectedUserId}
        title={t('please_confirm_action')}
        message={t('confirm_user_business_area_disassociate')}
        onConfirm={() => {
          if (unlinkSelectedUserId) {
            disassociateUserBusinessArea({
              businessAreaId,
              userId: unlinkSelectedUserId,
            });
          }
          setUnlinkSelectedUserId(undefined);
        }}
        handleClose={() => setUnlinkSelectedUserId(undefined)}
      />
    </>
  );
};
